import { spaceClient, spaceClientFor } from './client';

export const getAdvertisersForSpace = (spaceId) =>
  spaceClientFor(spaceId).get('/cms/advertisers').then((r) => r.data);

// ─── Advertisers  api/space/{spaceId}/advertisers ─────────────────────────────

export const getAdvertisers = () =>
  spaceClient.get('/cms/advertisers').then((r) => r.data);

export const getAdvertiserById = (id, spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get(`/cms/advertisers/${id}`).then((r) => r.data);

export const createAdvertiser = (data) =>
  spaceClient.post('/advertisers', {
    isActive:      data.isActive ?? true,
    industry:      data.industry  || null,
    modifiedBy:    data.modifiedBy || null,
    localizedName: data.localizedName,
  }).then((r) => r.data);

export const updateAdvertiser = (id, data) =>
  spaceClient.put(`/advertisers/${id}`, {
    isActive:      data.isActive  ?? null,
    industry:      data.industry  || null,
    modifiedBy:    data.modifiedBy || null,
    localizedName: data.localizedName ?? null,
  }).then((r) => r.data);

export const deleteAdvertiser = (id) =>
  spaceClient.delete(`/advertisers/${id}`);

// ─── Campaigns  api/space/{spaceId}/advertisers/{advertiserId}/campaigns ───────

export const getCampaigns = (advertiserId, spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get(`/cms/advertisers/${advertiserId}/campaigns`).then((r) => r.data);

export const getCampaignById = (advertiserId, id, spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get(`/cms/advertisers/${advertiserId}/campaigns/${id}`).then((r) => r.data);

export const createCampaign = (advertiserId, data) =>
  spaceClient.post(`/advertisers/${advertiserId}/campaigns`, {
    advertiserId,
    dailyRate:        data.dailyRate,
    durationModifier: data.durationModifier ?? 1.0,
    startDate:        data.startDate,
    endDate:          data.endDate,
    modifiedBy:       data.modifiedBy || null,
    localizedName:    data.localizedName,
  }).then((r) => r.data);

export const updateCampaign = (advertiserId, id, data) =>
  spaceClient.put(`/advertisers/${advertiserId}/campaigns/${id}`, {
    dailyRate:        data.dailyRate        ?? null,
    durationModifier: data.durationModifier ?? null,
    startDate:        data.startDate        ?? null,
    endDate:          data.endDate          ?? null,
    modifiedBy:       data.modifiedBy       || null,
    localizedName:    data.localizedName    ?? null,
    // Only set when the dates moved and the user chose what happens to the campaign's ads.
    adCleanup:        data.adCleanup        ?? null,
  }).then((r) => r.data);

// Which of the campaign's ads would lose run dates if it moved to [startDate, endDate]. Read-only.
export const getCampaignDateImpact = (advertiserId, id, startDate, endDate) =>
  spaceClient
    .get(`/advertisers/${advertiserId}/campaigns/${id}/date-change-impact`, { params: { startDate, endDate } })
    .then((r) => r.data);

export const deleteCampaign = (advertiserId, id) =>
  spaceClient.delete(`/advertisers/${advertiserId}/campaigns/${id}`);

// ─── Ads  api/space/{spaceId}/advertisers/{advertiserId}/campaigns/{campaignId}/ads ──

export const getAds = (advertiserId, campaignId, spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get(`/cms/advertisers/${advertiserId}/campaigns/${campaignId}/ads`).then((r) => r.data);

export const createAd = (advertiserId, campaignId, data) =>
  spaceClient.post(`/advertisers/${advertiserId}/campaigns/${campaignId}/ads`, {
    campaignId,
    placementId:     data.placementId,
    durationSeconds: data.durationSeconds,
    mediaTypeId:     data.mediaTypeId ?? 1,
    modifiedBy:      data.modifiedBy  || null,
    mediaLinks:      data.mediaLinks  ?? [],
    localizedName:   data.localizedName,
    scheduleDates:   data.scheduleDates ?? [],
    // modelId (3D ads only) and portal (a 3D ad's portal, or a 2D ad's click link) are sent when set, so a 2D ad without a click link
    // stays exactly what it was before 3D ads.
    ...(data.modelId != null ? { modelId: data.modelId } : {}),
    ...(data.portal ? { portal: data.portal } : {}),
  }).then((r) => r.data);

export const updateAd = (advertiserId, campaignId, id, data) =>
  spaceClient.put(`/advertisers/${advertiserId}/campaigns/${campaignId}/ads/${id}`, {
    placementId:     data.placementId     ?? null,
    durationSeconds: data.durationSeconds ?? null,
    mediaTypeId:     data.mediaTypeId     ?? null,
    modifiedBy:      data.modifiedBy      || null,
    mediaLinks:      data.mediaLinks      ?? null,
    localizedName:   data.localizedName   ?? null,
    scheduleDates:   data.scheduleDates   ?? null,
    // null = unchanged. The modal sends the model and the COMPLETE portal on every edit of a 3D ad (the stored request keeps
    // its nulls and an approver's snapshot would otherwise show the field as removed); { portalLinks: [] } removes the portal.
    // A 2D ad's portal is its click link and goes back on every 2D edit the same way.
    modelId:         data.modelId         ?? null,
    portal:          data.portal          ?? null,
  }).then((r) => r.data);

export const deleteAd = (advertiserId, campaignId, id) =>
  spaceClient.delete(`/advertisers/${advertiserId}/campaigns/${campaignId}/ads/${id}`);

// ─── Ad Placements  api/space/{spaceId}/ad-placements ────────────────────────

export const getAdPlacements = (spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get('/ad-placements').then((r) => r.data);

// ─── 3D models  api/space/{spaceId}/ad-3d-models (read only here: the registry is managed from Postman) ───────

// All models of the space, inactive ones included (the modal still shows an ad's current model after it was deactivated).
export const getAdModels = (spaceId) =>
  (spaceId ? spaceClientFor(spaceId) : spaceClient).get('/ad-3d-models').then((r) => r.data ?? []);

export const getPlacementAvailabilityPerDay = (startDate, endDate, excludeAdId = null, spaceId) => {
  const params = { startDate, endDate };
  if (excludeAdId != null) params.excludeAdId = excludeAdId;
  return (spaceId ? spaceClientFor(spaceId) : spaceClient)
    .get('/ad-placements/availability-per-day', { params })
    .then((r) => r.data ?? []);
};
